import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, RefreshCw, ShieldAlert, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { CopyIcon } from '@/components/CopyIcon';
import {
  getAdminSecretsGetAdminClientsIdGuidSecretsQueryKey,
  useAdminSecretsDeleteAdminClientsIdGuidSecretsSecretIdGuid,
  useAdminSecretsGetAdminClientsIdGuidSecrets,
  useAdminSecretsPostAdminClientsIdGuidSecretsRotate,
} from '@/lib/api/admin-secrets/admin-secrets';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useHasRole } from '@/lib/auth/useAuth';

interface Props {
  clientId: string;
}

/**
 * HMAC signing secrets for the client. The current secret is what partners
 * use to sign `/auth/token` requests; rotating issues a brand-new secret
 * (revealed once) AND keeps the old one active until you explicitly revoke
 * it, so partners can roll over without downtime.
 *
 * Admin-gated. The plaintext secret only exists on the response body of the
 * rotate mutation — once the reveal dialog closes, only the hashed
 * fingerprint remains.
 */
export function ClientSecretsCard({ clientId }: Props) {
  const isAdmin = useHasRole('Admin');
  const queryClient = useQueryClient();

  const [revealed, setRevealed] = useState<string | null>(null);
  const [revokeConfirm, setRevokeConfirm] = useState<{ id: string; fingerprint: string } | null>(null);

  const list = useAdminSecretsGetAdminClientsIdGuidSecrets(clientId, { query: { enabled: !!clientId } });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getAdminSecretsGetAdminClientsIdGuidSecretsQueryKey(clientId),
    });

  const rotate = useAdminSecretsPostAdminClientsIdGuidSecretsRotate({
    mutation: {
      onSuccess: async (data) => {
        await invalidate();
        setRevealed(data.plaintextSecret);
        toast.success('Secret rotated.');
      },
      onError: () => toast.error('Could not rotate secret.'),
    },
  });

  const revoke = useAdminSecretsDeleteAdminClientsIdGuidSecretsSecretIdGuid({
    mutation: {
      onSuccess: async () => {
        await invalidate();
        toast.success('Secret revoked.');
        setRevokeConfirm(null);
      },
      onError: () => toast.error('Could not revoke secret.'),
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-muted-foreground" />
            HMAC signing secrets
          </CardTitle>
          <CardDescription>
            Partners sign <code className="font-mono text-[11px]">/auth/token</code> requests with one of these.
            Rotate to issue a new one; old secrets remain valid until revoked so partners can roll over without downtime.
          </CardDescription>
        </div>
        {isAdmin && (
          <Button
            className="gap-2"
            onClick={() => rotate.mutate({ id: clientId })}
            disabled={rotate.isPending}
          >
            <RefreshCw className={`h-4 w-4 ${rotate.isPending ? 'animate-spin' : ''}`} />
            Rotate HMAC secret
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {!isAdmin && (
          <div className="flex items-center gap-2 border-t border-border px-6 py-3 text-xs text-muted-foreground">
            <ShieldAlert className="h-3 w-3" />
            Admin role required to view, rotate, or revoke secrets.
          </div>
        )}
        {list.isLoading ? (
          <div className="space-y-2 p-6">
            {Array.from({ length: 2 }, (_, i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : !list.data || list.data.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No secrets on file. Rotate to issue the first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 font-medium">Fingerprint</th>
                <th className="p-3 font-medium">Created</th>
                <th className="p-3 font-medium">Status</th>
                {isAdmin && <th className="w-32 p-3 text-right font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.data.map((s) => {
                const isRevoked = s.revokedAt != null;
                return (
                  <tr key={s.id} className="hover:bg-muted/30">
                    <td className="p-3 font-mono text-xs text-muted-foreground">{s.hashedFingerprint}</td>
                    <td className="p-3 text-xs tabular-nums">{new Date(s.createdAt).toLocaleString()}</td>
                    <td className="p-3">
                      {isRevoked ? (
                        <Badge variant="warning">Revoked</Badge>
                      ) : s.isActive ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="p-3 text-right">
                        {!isRevoked && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setRevokeConfirm({ id: s.id, fingerprint: s.hashedFingerprint })}
                            disabled={revoke.isPending}
                          >
                            <Trash2 className="h-4 w-4" /> Revoke
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>

      <SecretRevealDialog
        open={revealed !== null}
        secret={revealed ?? ''}
        onOpenChange={(open) => !open && setRevealed(null)}
      />

      <RevokeConfirmDialog
        open={revokeConfirm !== null}
        fingerprint={revokeConfirm?.fingerprint ?? ''}
        pending={revoke.isPending}
        onConfirm={() => {
          if (!revokeConfirm) return;
          revoke.mutate({ id: clientId, secretId: revokeConfirm.id });
        }}
        onOpenChange={(open) => !open && setRevokeConfirm(null)}
      />
    </Card>
  );
}

// ---- Dialogs ----------------------------------------------------------

interface SecretRevealDialogProps {
  open: boolean;
  secret: string;
  onOpenChange: (open: boolean) => void;
}

function SecretRevealDialog({ open, secret, onOpenChange }: SecretRevealDialogProps) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New HMAC signing secret</DialogTitle>
          <DialogDescription>
            Copy this now — it will <span className="font-medium">not be shown again</span>. Hand it to the partner
            via your usual secure channel. They use it to sign{' '}
            <code className="font-mono text-[11px]">POST /auth/token</code> and every{' '}
            <code className="font-mono text-[11px]">/proxy/*</code> request via{' '}
            <code className="font-mono text-[11px]">X-Signature</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3">
          <code className="flex-1 break-all font-mono text-xs">{secret}</code>
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={() => {
              navigator.clipboard.writeText(secret);
              setCopied(true);
              toast.success('Copied to clipboard.');
            }}
          >
            <CopyIcon copied={copied} onRevert={() => setCopied(false)} className="h-3 w-3" />
            Copy
          </Button>
        </div>

        <p className="text-[11px] text-muted-foreground">
          The previous secret(s) remain active until you revoke them, so partners can roll over without
          downtime. Revoke from the table above once the partner confirms they've switched.
        </p>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RevokeConfirmDialogProps {
  open: boolean;
  fingerprint: string;
  pending: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

function RevokeConfirmDialog({ open, fingerprint, pending, onConfirm, onOpenChange }: RevokeConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke this secret?</DialogTitle>
          <DialogDescription>
            Any partner still signing requests with this secret will start receiving{' '}
            <code className="font-mono text-[11px]">401 auth.signature_invalid</code>. Only do this after
            they've confirmed they've moved to a new secret.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border bg-muted/30 p-3 font-mono text-xs">
          {fingerprint}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={pending}>
            Revoke
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
